/* eslint-disable @typescript-eslint/no-unused-vars */
import { UilCheckCircle } from "@iconscout/react-unicons";
import Unicon from "components/base/Unicon";
import { Card, Col, Container, Row } from "react-bootstrap";
import "./AuthCardLayout.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt, faChartLine, faBrain, faRocket, faMicrochip, faNetworkWired, faCogs } from "@fortawesome/free-solid-svg-icons";
import bg37 from "assets/img/bg/37.png";
import bg38 from "assets/img/bg/38.png";
import authIllustrations from "assets/img/spot-illustrations/auth.png";
import authIllustrationsDark from "assets/img/spot-illustrations/auth-dark.png";
import { PropsWithChildren } from "react";
import { Link } from "react-router-dom";
import Logo from "components/common/Logo";
import classNames from "classnames";

interface AuthCardLayoutProps {
  logo?: boolean;
  className?: string;
}

const AuthCardLayout = ({
  logo = true,
  className,
  children,
}: PropsWithChildren<AuthCardLayoutProps>) => {
  return (
    <Container fluid style={{
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 30%, #cbd5e1 70%, #94a3b8 100%)',
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Subtle AI Network Background */}
      <div 
        className="position-absolute w-100 h-100"
        style={{
          backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(0, 212, 255, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(16, 185, 129, 0.03) 0%, transparent 50%), radial-gradient(circle at 50% 50%, rgba(102, 126, 234, 0.02) 0%, transparent 50%)',
          animation: 'ai-glow-pulse 12s infinite alternate'
        }}
      />

      <Row className="flex-center position-relative min-vh-100 g-0 py-5">
        <Col xs={11} sm={10} xl={10} xxl={9}>
          <Card 
            className="border-0 overflow-hidden"
            style={{
              background: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.08), 0 0 40px rgba(0, 212, 255, 0.05)',
              minHeight: '650px'
            }}
          >
            <Card.Body className="p-0">
              <Row className="h-100 gx-0">
                {/* Left Side - AI Branding & Features */}
                <Col
                  lg={7}
                  xl={8}
                  className="position-relative overflow-hidden d-flex flex-column"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
                    backdropFilter: 'blur(15px)',
                    borderRadius: '24px 0 0 24px',
                    minHeight: '650px'
                  }}
                >
                  {/* Subtle AI Accent Overlay */}
                  <div
                    className="position-absolute w-100 h-100"
                    style={{
                      background: 'linear-gradient(45deg, rgba(0, 212, 255, 0.02) 0%, transparent 50%, rgba(16, 185, 129, 0.02) 100%)',
                      animation: 'ai-pulse 4s infinite'
                    }}
                  />
                  
                  <div className="position-relative flex-grow-1 d-flex flex-column justify-content-center px-4 px-lg-6 py-5">
                    {/* AI Intelligence Header */}
                    <div className="d-flex align-items-center mb-4">
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{ 
                          width: "56px", 
                          height: "56px",
                          background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                          boxShadow: '0 0 25px rgba(0, 212, 255, 0.3)',
                          animation: 'ai-glow-pulse 3s infinite'
                        }}
                      >
                        <FontAwesomeIcon 
                          icon={faBolt} 
                          className="text-white" 
                          size="xl"
                          style={{ filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.8))' }}
                        />
                      </div>
                      <div>
                        <h3 
                          className="mb-1 fw-bold"
                          style={{ 
                            color: '#1e293b', 
                            fontSize: '2.2rem',
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            letterSpacing: '-0.5px'
                          }}
                        >
                          KairosIQ AI
                        </h3>
                        <div 
                          className="d-flex align-items-center"
                          style={{
                            background: 'rgba(16, 185, 129, 0.1)',
                            padding: '6px 12px',
                            borderRadius: '15px',
                            border: '1px solid rgba(16, 185, 129, 0.2)',
                            display: 'inline-flex',
                            width: 'fit-content'
                          }}
                        >
                          <div 
                            className="rounded-circle me-2"
                            style={{
                              width: '6px',
                              height: '6px',
                              background: '#10b981',
                              animation: 'ai-pulse 2s infinite'
                            }}
                          />
                          <span 
                            className="fw-semibold"
                            style={{ 
                              fontSize: '11px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              color: '#10b981'
                            }}
                          >
                            Neural Network Active
                          </span>
                        </div>
                      </div>
                    </div>

                    <p 
                      className="mb-4"
                      style={{ 
                        color: '#475569',
                        fontSize: '1.2rem',
                        lineHeight: '1.6',
                        fontWeight: '400'
                      }}
                    >
                      Access the future of real estate intelligence. Our AI-powered platform transforms how you{" "}
                      <span style={{ color: '#00d4ff', fontWeight: '600' }}>manage leads</span>,{" "}
                      <span style={{ color: '#10b981', fontWeight: '600' }}>predict markets</span>, and{" "}
                      <span style={{ color: '#f59e0b', fontWeight: '600' }}>close deals</span>.
                    </p>

                    {/* AI Feature Cards - Horizontal Layout */}
                    <div className="mb-4">
                      <Row className="g-3">
                        <Col md={4}>
                          <div 
                            className="d-flex flex-column align-items-center text-center p-3 rounded-3 h-100"
                            style={{
                              background: 'rgba(0, 212, 255, 0.06)',
                              border: '1px solid rgba(0, 212, 255, 0.15)',
                              backdropFilter: 'blur(10px)'
                            }}
                          >
                            <div 
                              className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                              style={{
                                width: '48px', 
                                height: '48px',
                                background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)'
                              }}
                            >
                              <FontAwesomeIcon icon={faChartLine} className="text-white" size="lg" />
                            </div>
                            <div>
                              <div className="fw-bold mb-2" style={{ fontSize: '1rem', color: '#1e293b' }}>
                                Intelligent Lead Scoring
                              </div>
                              <small style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                AI analyzes 50+ data points in real-time
                              </small>
                            </div>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div 
                            className="d-flex flex-column align-items-center text-center p-3 rounded-3 h-100"
                            style={{
                              background: 'rgba(16, 185, 129, 0.06)',
                              border: '1px solid rgba(16, 185, 129, 0.15)',
                              backdropFilter: 'blur(10px)'
                            }}
                          >
                            <div 
                              className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                              style={{
                                width: '48px', 
                                height: '48px',
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                              }}
                            >
                              <FontAwesomeIcon icon={faBrain} className="text-white" size="lg" />
                            </div>
                            <div>
                              <div className="fw-bold mb-2" style={{ fontSize: '1rem', color: '#1e293b' }}>
                                Predictive Market Analysis
                              </div>
                              <small style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                Machine learning forecasts with 94% accuracy
                              </small>
                            </div>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div 
                            className="d-flex flex-column align-items-center text-center p-3 rounded-3 h-100"
                            style={{
                              background: 'rgba(245, 158, 11, 0.06)',
                              border: '1px solid rgba(245, 158, 11, 0.15)',
                              backdropFilter: 'blur(10px)'
                            }}
                          >
                            <div 
                              className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                              style={{
                                width: '48px', 
                                height: '48px',
                                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                              }}
                            >
                              <FontAwesomeIcon icon={faRocket} className="text-white" size="lg" />
                            </div>
                            <div>
                              <div className="fw-bold mb-2" style={{ fontSize: '1rem', color: '#1e293b' }}>
                                Automated Workflows
                              </div>
                              <small style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                Save 15+ hours per week with smart automation
                              </small>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Col>

                {/* Right Side - Login Form */}
                <Col 
                  lg={5}
                  xl={4}
                  className="d-flex flex-column justify-content-center"
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '0 24px 24px 0',
                    minHeight: '650px'
                  }}
                >
                  <div className="auth-wrapper px-4 py-5">
                    <div className="auth-form-box">
                      {children}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Enhanced AI Animation Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes ai-pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          
          @keyframes ai-glow-pulse {
            0%, 100% { 
              box-shadow: 0 0 20px rgba(0, 212, 255, 0.3);
              filter: drop-shadow(0 0 10px rgba(0, 212, 255, 0.2));
            }
            50% { 
              box-shadow: 0 0 30px rgba(0, 212, 255, 0.4);
              filter: drop-shadow(0 0 15px rgba(0, 212, 255, 0.3));
            }
          }
          
          @keyframes ai-float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-5px); }
          }

          @media (max-width: 991.98px) {
            .auth-card-row {
              flex-direction: column;
            }
          }
        `
      }} />
    </Container>
  );
};

export default AuthCardLayout;
